import { useCallback, useEffect, useRef, useState } from 'react';

// Drag-handle Before/After image comparator (Phase 10-5).
// No library — uses CSS `clip-path: inset(...)` on the after image so both
// images stay the same size and the visible slice always lines up.
//
// Convention: 0% = original 전체, 100% = AI 디자인 전체. 왼쪽부터 AI 디자인이
// 덮어 나간다.
//
// 기본값은 100 — 이 화면의 일은 크레딧 주고 산 결과물을 건네주는 것이다.
// 예전 기본값 50 은 분할선이 화면 정가운데, 즉 구도상 피사체가 있는 자리를
// 정확히 잘라서 원본도 디자인도 방으로 안 읽혔다.
//
// revealOnMount 를 주면 0 에서 시작해 100 까지 한 번 쓸고 멈춘다. 변화를
// 정지된 반반보다 잘 보여주면서 끝나는 자리는 완성본이고, 그 과정에서
// 슬라이더가 있다는 것도 알려준다 (100 에서는 핸들만 남고 Original 라벨이
// 숨겨져서 원본의 존재 단서가 사라지기 때문에 이게 필요하다).
// 처음 보는 사람에게만 — 생성 직후(ResultStep)와 공유 링크(ShareView).
// 보관함에서 다시 여는 상세(DesignDetail)는 이미 본 것이라 바로 100.
//
// 타이밍: 원본을 잠깐 보여주고(HOLD) 천천히 쓴다(REVEAL). 처음엔 800ms 에
// ease-out 이었는데, ease-out 은 시작이 제일 빨라서 절반이 첫 250ms 에
// 지나가버렸다 — "슥" 하고 끝나 슬라이더가 움직였다는 것조차 안 보였다.
// 느리게, 그리고 등속에 가깝게 가야 "이게 밀려서 바뀌는 것" 으로 읽힌다.
const REVEAL_HOLD_MS = 600;   // 쓸기 전 원본을 보여주는 시간
const REVEAL_MS = 2800;       // 0 → 100 에 걸리는 시간

export function BeforeAfterSlider({ beforeSrc, afterSrc, beforeLabel, afterLabel, alt = '', revealOnMount = false }) {
  const containerRef = useRef(null);
  const beforeImgRef = useRef(null);
  const afterImgRef = useRef(null);
  const draggingRef = useRef(false);
  // 모션을 줄이라는 설정이면 쓸기 없이 바로 결과물 — 애니메이션은 부가 연출이라
  // 생략해도 화면이 하는 일은 그대로다.
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const willReveal = revealOnMount && !prefersReducedMotion;
  const [percent, setPercent] = useState(willReveal ? 0 : 100);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Match container to the natural image aspect so portrait/square shots
  // aren't cropped to a forced 4:3 box (CSS default). AI preserves input
  // aspect, so before/after match; whichever <img> reports natural size
  // first wins. The other one is identical so the slider reveal still
  // aligns pixel-for-pixel.
  const [aspectRatio, setAspectRatio] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const handleAspectFromImg = useCallback((e) => {
    setLoadedCount(n => n + 1);
    if (aspectRatio) return;
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
    }
  }, [aspectRatio]);

  // 두 장 다 뜨면 0 → 100 으로 한 번 쓸고 끝. 사용자가 그 전에 손을 대면
  // (percent 가 0 이 아니면) 연출을 포기하고 조작을 우선한다.
  const revealDoneRef = useRef(false);
  // 준비 판정은 onLoad 카운트가 아니라 img.complete 로 한다.
  //   · 캐시에서 온 이미지는 React 가 핸들러를 붙이기 전에 이미 complete 라
  //     onLoad 가 안 뜬다 → 카운트만 보면 영영 2 에 못 닿는다.
  //   · 반대로 느린 망에서 "일정 시간 뒤 그냥 시작" 하는 맹목 타이머를 쓰면
  //     사진이 아직 안 그려졌는데 쓸기가 끝나버려서, 사용자 눈에는 연출이
  //     아예 없었던 것처럼 보인다. (이게 실제로 났던 문제다.)
  // 그래서 complete 를 폴링하고, 타이머는 정말 마지막 안전망으로만 길게 둔다.
  const [imagesReady, setImagesReady] = useState(false);
  useEffect(() => {
    if (!willReveal || imagesReady) return;
    const ready = () => !!(beforeImgRef.current?.complete && afterImgRef.current?.complete
      && beforeImgRef.current.naturalWidth && afterImgRef.current.naturalWidth);
    if (ready()) { setImagesReady(true); return; }
    const iv = setInterval(() => { if (ready()) { setImagesReady(true); clearInterval(iv); } }, 100);
    // 이미지가 끝내 안 뜨면(404 등) 연출을 영영 안 하느니 그냥 결과물을 보여준다.
    const bail = setTimeout(() => { setImagesReady(true); clearInterval(iv); }, 10000);
    return () => { clearInterval(iv); clearTimeout(bail); };
  }, [willReveal, imagesReady, loadedCount]);

  useEffect(() => {
    if (!willReveal || revealDoneRef.current || !imagesReady) return;
    revealDoneRef.current = true;
    setIsRevealing(true);
    // HOLD 동안 원본을 보여준 뒤 쓸기 시작 — 바로 움직이면 "원본이 있었다" 를
    // 인지할 틈이 없다.
    const start = setTimeout(() => setPercent(100), REVEAL_HOLD_MS);
    const done = setTimeout(() => setIsRevealing(false), REVEAL_HOLD_MS + REVEAL_MS + 50);
    return () => { clearTimeout(start); clearTimeout(done); };
  }, [willReveal, imagesReady]);

  // 쓸기 중에 사용자가 실제로 **조작하면** 연출을 접는다.
  // 주의: 이 함수는 .ba-revealing 클래스만 떼며, 이미 달리고 있는 transition
  // 을 되돌리진 못한다 (실행 중 transition 은 duration 을 바꿔도 원래 타이밍
  // 으로 끝난다). 진짜로 끊는 건 드래그 커밋 시 붙는 .ba-no-transition 이다.
  // 그래서 pointerdown 에서는 부르지 않는다 — 모바일에선 스크롤하려고 손가락
  // 을 대기만 해도 pointerdown 이 나는데, 거기서 상태를 흔들 이유가 없다.
  const stopReveal = useCallback(() => {
    if (!revealDoneRef.current) revealDoneRef.current = true;
    setIsRevealing(false);
  }, []);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPercent(Math.max(0, Math.min(100, next)));
  }, []);

  // Direction-aware pointer flow — defers capture until we know the user
  // intends a horizontal drag, so vertical swipes pass through to the page
  // scroll. Paired with `touch-action: pan-y` in CSS so the browser handles
  // vertical scrolling natively before our handlers even need to decide.
  const startRef = useRef(null); // { x, y, decided }
  const DIRECTION_THRESHOLD = 6; // px before we commit to an axis

  const handlePointerDown = (e) => {
    startRef.current = { x: e.clientX, y: e.clientY, decided: false };
    // No setPointerCapture / setIsDragging yet — wait for direction.
  };
  const handlePointerMove = (e) => {
    const s = startRef.current;
    if (!s) return;
    if (!s.decided) {
      const dx = Math.abs(e.clientX - s.x);
      const dy = Math.abs(e.clientY - s.y);
      if (dx < DIRECTION_THRESHOLD && dy < DIRECTION_THRESHOLD) return;
      if (dy > dx) {
        // Vertical intent — release to browser scroll, abort gesture.
        startRef.current = null;
        return;
      }
      // Horizontal — commit. 여기서부터가 진짜 조작이다.
      stopReveal();
      s.decided = true;
      draggingRef.current = true;
      setIsDragging(true);
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    }
    updateFromClientX(e.clientX);
  };
  const handlePointerUp = (e) => {
    const s = startRef.current;
    // Tap (no movement, never committed) → jump slider to clicked x.
    if (s && !s.decided) { stopReveal(); updateFromClientX(e.clientX); }
    draggingRef.current = false;
    setIsDragging(false);
    startRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
  };
  const handlePointerCancel = (e) => {
    // Browser took over (e.g. scroll) — reset everything.
    draggingRef.current = false;
    setIsDragging(false);
    startRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
  };

  // Keyboard support — focus the handle and use ←/→. 5% steps, 1% with Shift.
  const handleKeyDown = (e) => {
    stopReveal();
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPercent(p => Math.max(0, p - (e.shiftKey ? 1 : 5)));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPercent(p => Math.min(100, p + (e.shiftKey ? 1 : 5)));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setPercent(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setPercent(100);
    }
  };

  // Cancel drag if the window loses focus mid-drag.
  useEffect(() => {
    const cancel = () => { draggingRef.current = false; };
    window.addEventListener('blur', cancel);
    return () => window.removeEventListener('blur', cancel);
  }, []);

  return (
    <div
      ref={containerRef}
      className="ba-slider"
      style={aspectRatio ? { aspectRatio } : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <img
        ref={beforeImgRef}
        src={beforeSrc}
        alt={alt ? `${alt} — ${beforeLabel}` : beforeLabel}
        className="ba-img"
        draggable={false}
        onLoad={handleAspectFromImg}
      />
      <img
        ref={afterImgRef}
        src={afterSrc}
        alt={alt ? `${alt} — ${afterLabel}` : afterLabel}
        className={`ba-img ba-img-after${isDragging ? ' ba-no-transition' : ''}${isRevealing ? ' ba-revealing' : ''}`}
        draggable={false}
        onLoad={handleAspectFromImg}
        style={{ clipPath: `inset(0 ${100 - percent}% 0 0)` }}
      />

      {/* 슬라이더가 양쪽 끝에 있을 때 — 보이지 않는 쪽 이미지의 라벨도 hide.
         percent=0 (전체 Original) → AI Design 라벨 hide.
         percent=100 (전체 AI Design) → Original 라벨 hide.

         쓸기 중엔 둘 다 숨긴다. percent 는 시작하자마자 100 이 되고 화면만
         800ms 동안 따라오기 때문에, 그 사이엔 아직 원본이 보이는데 라벨은
         "AI Design" 이라고 떠 있게 된다. 움직이는 동안은 라벨이 없는 편이
         조용하기도 하다. */}
      {beforeLabel && !isRevealing && percent < 100 && <span className="ba-label ba-label-before">{beforeLabel}</span>}
      {afterLabel && !isRevealing && percent > 0 && <span className="ba-label ba-label-after">{afterLabel}</span>}

      <div
        role="slider"
        aria-label="Before / After comparison"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percent)}
        tabIndex={0}
        className={`ba-handle${isDragging ? ' ba-no-transition' : ''}${isRevealing ? ' ba-revealing' : ''}`}
        style={{ left: `${percent}%` }}
        onKeyDown={handleKeyDown}
      >
        <div className="ba-handle-line" />
        <div className="ba-handle-knob">
          <i className="material-symbols-rounded">chevron_left</i>
          <i className="material-symbols-rounded">chevron_right</i>
        </div>
      </div>
    </div>
  );
}
